/* ============================================================
   LIGHT & WELLNESS CO. — THEME JAVASCRIPT
   ============================================================ */

'use strict';

// ── UTILITY ─────────────────────────────────────────────────────
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function setBodyLock(locked) {
  if (locked) {
    document.body.classList.add('is-drawer-open');
    return;
  }

  requestAnimationFrame(() => {
    const hasOpenOverlay = document.querySelector('.mobile-nav.is-open, .search-drawer.is-open, .cart-drawer.is-open, .quick-view-modal.is-open');
    document.body.classList.toggle('is-drawer-open', Boolean(hasOpenOverlay));
  });
}

function formatMoney(cents) {
  const value = Number(cents) || 0;
  return '$' + (value / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function renderCartCount(cart) {
  $$('.js-cart-count').forEach((el) => {
    el.textContent = cart.item_count;
    el.style.display = cart.item_count > 0 ? 'flex' : 'none';
  });
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const PREORDER_LINE_PROPERTY = 'Pre-order item';
const PREORDER_NOTE_PREFIX = 'Pre-order notice:';

function buildPreorderLineMessage(preorderDate = '') {
  return `${preorderDate ? `Expected to ship from ${preorderDate}. ` : ''}Your complete order will ship when the pre-order is ready.`;
}

function preorderLineProperties(preorderDate = '') {
  return { [PREORDER_LINE_PROPERTY]: buildPreorderLineMessage(preorderDate) };
}

function getPreorderLineMessage(item) {
  return item?.properties?.[PREORDER_LINE_PROPERTY] || '';
}

function isPreorderItem(item) {
  return Boolean(getPreorderLineMessage(item));
}

function buildPreorderOrderNote(cart) {
  const messages = [...new Set((cart?.items || []).map(getPreorderLineMessage).filter(Boolean))];
  if (!messages.length) return '';
  return `${PREORDER_NOTE_PREFIX} This order contains a pre-order product. ${messages.join(' ')}`;
}

async function syncPreorderOrderNote(cart = null) {
  try {
    const currentCart = cart || await fetch('/cart.js').then((res) => res.json());
    const note = buildPreorderOrderNote(currentCart);
    const currentNote = currentCart.note || '';

    if (!note && !currentNote.startsWith(PREORDER_NOTE_PREFIX)) return currentCart;
    if (currentNote === note) return currentCart;

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note })
    });

    return res.ok ? res.json() : currentCart;
  } catch {
    return cart;
  }
}

function safeStoreUrl(value, fallback = '#') {
  try {
    const url = new URL(String(value || ''), window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) return fallback;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

function safeImageUrl(value) {
  try {
    const url = new URL(String(value || ''), window.location.origin);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
}

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
}

const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
let activeFocusTrap = null;

function activateFocusTrap(container, initialFocus) {
  if (!container) return;
  deactivateFocusTrap(false);

  const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = $$(focusableSelector, container).filter(element => element.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  container.addEventListener('keydown', onKeydown);
  activeFocusTrap = { container, onKeydown, returnFocus };
  requestAnimationFrame(() => (initialFocus || $(focusableSelector, container) || container).focus());
}

function deactivateFocusTrap(restoreFocus = true, expectedContainer = null) {
  if (!activeFocusTrap) return;
  if (expectedContainer && activeFocusTrap.container !== expectedContainer) return;
  const { container, onKeydown, returnFocus } = activeFocusTrap;
  container.removeEventListener('keydown', onKeydown);
  activeFocusTrap = null;
  if (restoreFocus && returnFocus?.isConnected && returnFocus.offsetParent !== null) requestAnimationFrame(() => returnFocus.focus());
}

// ── ANNOUNCEMENT BAR ────────────────────────────────────────────
function initAnnouncementBar() {
  const bar   = $('.announcement-bar');
  const close = $('.announcement-bar__close');
  if (!bar) {
    updateHeaderOffset(0);
    return;
  }

  const dismissed = sessionStorage.getItem('announcementDismissed');
  if (dismissed) {
    bar.style.display = 'none';
    updateHeaderOffset(0);
    return;
  }

  updateHeaderOffset(bar.offsetHeight);

  if (!close) return;

  close.addEventListener('click', () => {
    bar.style.height = bar.offsetHeight + 'px';
    requestAnimationFrame(() => {
      bar.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      bar.style.height     = '0';
      bar.style.opacity    = '0';
      bar.style.overflow   = 'hidden';
    });
    bar.addEventListener('transitionend', () => {
      bar.style.display = 'none';
      const header = $('.site-header');
      if (header) header.style.top = '0px';
      updateHeaderOffset(0);
    }, { once: true });
    sessionStorage.setItem('announcementDismissed', '1');
  });
}

function updateHeaderOffset(barHeight) {
  const header = $('.site-header');
  const headerH = header ? header.offsetHeight : 72;
  document.documentElement.style.setProperty('--header-height', headerH + 'px');
  document.documentElement.style.setProperty('--content-offset', (headerH + barHeight) + 'px');
  document.documentElement.style.setProperty('--header-stack-height', (headerH + barHeight) + 'px');
}

// ── STICKY HEADER ───────────────────────────────────────────────
function initStickyHeader() {
  const header = $('.site-header');
  if (!header) return;

  let ticking = false;

  function update() {
    const scrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    const bar = $('.announcement-bar');
    const barH = (bar && bar.offsetHeight && bar.style.display !== 'none') ? bar.offsetHeight : 0;

    header.classList.toggle('is-scrolled', scrollY > 60);

    // Slide header down below announcement bar until user scrolls past it
    const headerTop = Math.max(0, barH - Math.min(scrollY, barH));
    header.style.top = headerTop + 'px';
    updateHeaderOffset(barH);
    document.documentElement.style.setProperty('--header-stack-height', (header.offsetHeight + headerTop) + 'px');
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });

  update();
}

// ── ACCOUNT MENU ──────────────────────────────────────────────
function initAccountMenu() {
  const account = $('.site-header__account');
  const toggle = $('[data-account-toggle]', account);
  if (!account || !toggle) return;

  function setOpen(open) {
    account.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
  }

  toggle.addEventListener('click', (event) => {
    event.stopPropagation();
    if (window.matchMedia('(max-width: 1024px)').matches) {
      window.location.href = toggle.dataset.accountUrl;
      return;
    }
    setOpen(!account.classList.contains('is-open'));
  });

  document.addEventListener('click', (event) => {
    if (!account.contains(event.target)) setOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      toggle.focus();
    }
  });
}

// ── MOBILE NAV ──────────────────────────────────────────────────
function initMobileNav() {
  const toggle  = $('.site-header__mobile-toggle');
  const nav     = $('.mobile-nav');
  const overlay = $('.mobile-nav__overlay');
  const close   = $('.mobile-nav__close');
  if (!toggle || !nav) return;

  function open() {
    nav.classList.add('is-open');
    nav.setAttribute('aria-hidden', 'false');
    nav.removeAttribute('inert');
    toggle.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    setBodyLock(true);
    activateFocusTrap(nav.querySelector('.mobile-nav__drawer'), close);
  }

  function closeFn() {
    if (!nav.classList.contains('is-open')) return;
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    nav.setAttribute('inert', '');
    toggle.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    setBodyLock(false);
    deactivateFocusTrap(true, nav.querySelector('.mobile-nav__drawer'));
  }

  toggle.addEventListener('click', () => {
    if (nav.classList.contains('is-open')) closeFn();
    else open();
  });

  overlay?.addEventListener('click', closeFn);
  close?.addEventListener('click', closeFn);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeFn();
  });

  $$('[data-mobile-nav-toggle]', nav).forEach((button) => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;

    button.addEventListener('click', () => {
      const shouldOpen = button.getAttribute('aria-expanded') !== 'true';

      $$('[data-mobile-nav-toggle]', nav).forEach((otherButton) => {
        const otherPanel = document.getElementById(otherButton.getAttribute('aria-controls'));
        otherButton.setAttribute('aria-expanded', 'false');
        otherPanel?.classList.remove('is-open');
      });

      button.setAttribute('aria-expanded', String(shouldOpen));
      panel.classList.toggle('is-open', shouldOpen);
    });
  });
}

// ── SEARCH QUERY REWRITE ───────────────────────────────────────
// Shopify search matches whole words, so "lvl up" never finds the vendor
// "LVLUP Health". Known spellings are rewritten up front; anything else that
// returns nothing is retried with its words joined ("co q10" → "coq10") or
// letters/digits split ("omega3" → "omega 3").
const SEARCH_ALIASES = [
  [/\b(?:lvl|level)[\s\-_]*up(?:\s*health)?\b/gi, 'lvlup'],
  [/\bco[\s\-]*q[\s\-]*10\b/gi, 'coq10'],
  [/\btb[\s\-]*(?:4|500)\b/gi, 'tb4'],
];

function rewriteSearchQuery(query) {
  let out = String(query || '').trim().replace(/\s+/g, ' ');
  SEARCH_ALIASES.forEach(([pattern, replacement]) => { out = out.replace(pattern, replacement); });
  return out;
}

function searchFallbacks(query) {
  const candidates = [
    query.replace(/[\s\-]+/g, ''),
    query.replace(/([a-z])(\d)/gi, '$1 $2').replace(/(\d)([a-z])/gi, '$1 $2'),
    query.replace(/-/g, ' '),
  ];
  return [...new Set(candidates.map(c => c.trim()))].filter(c => c && c.toLowerCase() !== query.toLowerCase());
}

async function fetchProductSuggestions(query, limit) {
  const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${limit}&resources[options][unavailable_products]=last`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Search request failed');
  const data = await res.json();
  return data?.resources?.results?.products || [];
}

// Returns the products for the best spelling of a query, and that spelling.
async function smartProductSearch(rawQuery, limit = 6) {
  const query = rewriteSearchQuery(rawQuery);
  const products = await fetchProductSuggestions(query, limit);
  if (products.length) return { query, products };
  for (const alt of searchFallbacks(query)) {
    const altProducts = await fetchProductSuggestions(alt, limit);
    if (altProducts.length) return { query: alt, products: altProducts };
  }
  return { query, products: [] };
}

function searchUrl(query, extra = '') {
  return `/search?q=${encodeURIComponent(query)}&type=product&options[prefix]=last&options[unavailable_products]=last${extra}`;
}

// Every search form submits the rewritten query.
function initSearchForms() {
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('role') !== 'search') return;
    const input = form.querySelector('input[name="q"]');
    if (input) input.value = rewriteSearchQuery(input.value);
  });
}

// Search page: apply aliases to links that land here directly, and when a
// search comes back empty, jump to a spelling that has results.
async function initSearchPageRewrite() {
  const root = $('[data-search-page]');
  if (!root) return;
  const params = new URLSearchParams(window.location.search);
  const terms = (params.get('q') || '').trim();
  if (!terms || params.has('exact')) return;

  const from = params.get('from');
  if (from) {
    const note = $('[data-search-rewrite-note]', root);
    if (note) {
      note.innerHTML = `Showing results for <strong>${escapeHtml(terms)}</strong>. <a href="${escapeHtml(searchUrl(from, '&exact=1'))}">Search instead for “${escapeHtml(from)}”</a>`;
      note.hidden = false;
    }
    return;
  }

  const rewritten = rewriteSearchQuery(terms);
  if (rewritten.toLowerCase() !== terms.toLowerCase()) {
    window.location.replace(searchUrl(rewritten, `&from=${encodeURIComponent(terms)}`));
    return;
  }

  if (root.dataset.resultsCount !== '0') return;
  try {
    const { query, products } = await smartProductSearch(terms, 1);
    if (products.length && query.toLowerCase() !== terms.toLowerCase()) {
      window.location.replace(searchUrl(query, `&from=${encodeURIComponent(terms)}`));
    }
  } catch {}
}

// ── SEARCH DRAWER ──────────────────────────────────────────────
function initSearchDrawer() {
  const drawer = $('.search-drawer');
  const panel = $('.search-drawer__panel', drawer);
  const input = $('[data-search-input]', drawer);
  const results = $('[data-search-results]', drawer);
  const toggles = $$('[data-search-toggle]');
  const closes = $$('[data-search-close]', drawer);
  if (!drawer || !panel || !input || !results || !toggles.length) return;

  let debounce = null;
  let latestRequest = 0;

  function open() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    setBodyLock(true);
    activateFocusTrap(panel, input);
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    setBodyLock(false);
    deactivateFocusTrap(true, panel);
  }

  function renderSuggestions() {
    results.setAttribute('aria-busy', 'false');
    results.innerHTML = `
      <div class="search-drawer__suggestions">
        <a href="/collections/supplements">Supplements</a>
        <a href="/collections/featured">Our Top Picks</a>
        <a href="/collections/light-products">Light</a>
        <a href="/collections/water-products">Water</a>
        <a href="/collections/books">Books</a>
        <a href="/collections/frontpage?filter.p.m.custom.product_type=Sleep">Sleep</a>
      </div>
    `;
  }

  function formatSearchMoney(amount) {
    const value = Number.parseFloat(amount);
    if (!Number.isFinite(value)) return '';
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  async function fetchResults(query) {
    results.setAttribute('aria-busy', 'true');
    results.innerHTML = `<div class="search-drawer__status">Searching…</div>`;
    const requestId = ++latestRequest;
    try {
      const { query: matchedQuery, products } = await smartProductSearch(query, 6);
      if (requestId !== latestRequest) return;
      const safeQuery = escapeHtml(query);
      const allResultsUrl = escapeHtml(searchUrl(matchedQuery));

      if (!products.length) {
        results.innerHTML = `
          <div class="search-drawer__status">No products found for “${safeQuery}”.</div>
          <a class="search-drawer__all-link" href="${allResultsUrl}">View all search results</a>
        `;
        return;
      }

      results.innerHTML = `
        <div class="search-drawer__list">
          ${products.map(product => {
            const productUrl = escapeHtml(safeStoreUrl(product.url));
            const imageUrl = escapeHtml(safeImageUrl(product.featured_image?.url));
            const title = escapeHtml(product.title);
            return `
            <a class="search-drawer__item" href="${productUrl}">
              <span class="search-drawer__thumb">
                ${imageUrl ? `<img src="${imageUrl}" alt="${title}" width="72" height="72" loading="lazy">` : ''}
              </span>
              <span>
                <span class="search-drawer__item-title">${title}</span>
                <span class="search-drawer__item-meta">${escapeHtml(product.vendor || '')}</span>
                <span class="search-drawer__item-price">${formatSearchMoney(product.price)}</span>
              </span>
            </a>
          `; }).join('')}
        </div>
        <a class="search-drawer__all-link" href="${allResultsUrl}">View all results</a>
      `;
    } catch {
      if (requestId === latestRequest) results.innerHTML = `<div class="search-drawer__status">Search is unavailable right now.</div>`;
    } finally {
      if (requestId === latestRequest) results.setAttribute('aria-busy', 'false');
    }
  }

  toggles.forEach(btn => btn.addEventListener('click', open));
  closes.forEach(btn => btn.addEventListener('click', close));
  input.addEventListener('input', () => {
    const query = input.value.trim();
    clearTimeout(debounce);
    if (!query) {
      latestRequest++;
      renderSuggestions();
      return;
    }
    debounce = setTimeout(() => fetchResults(query), 180);
  });

  document.addEventListener('keydown', (e) => {
    const tag = document.activeElement?.tagName;
    const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag);
    if (e.key === '/' && !typing && !drawer.classList.contains('is-open')) {
      e.preventDefault();
      open();
    }
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  renderSuggestions();
}

// ── CART DRAWER ────────────────────────────────────────────────
function initCartDrawer() {
  const drawer = $('.cart-drawer');
  const body = $('[data-cart-drawer-body]', drawer);
  const footer = $('[data-cart-drawer-footer]', drawer);
  const subtotal = $('[data-cart-drawer-subtotal]', drawer);
  const progress = $('[data-cart-progress]', drawer);
  const toggles = $$('[data-cart-toggle]');
  const closes = $$('[data-cart-close]', drawer);
  if (!drawer || !body || !footer || !subtotal || !toggles.length) return;

  const freeShippingThreshold = 15000;
  let cachedCart = null;

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  function open() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    setBodyLock(true);
    activateFocusTrap($('.cart-drawer__panel', drawer), $('.cart-drawer__close', drawer));
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    setBodyLock(false);
    deactivateFocusTrap(true, $('.cart-drawer__panel', drawer));
  }

  function renderProgress(cart) {
    if (!progress) return;

    const remaining = Math.max(0, freeShippingThreshold - cart.total_price);
    const percent = Math.min(100, Math.round((cart.total_price / freeShippingThreshold) * 100));
    const hasLitewater = cart.items.some((item) => {
      const title = String(item.product_title || '').toLowerCase();
      const handle = String(item.handle || '').toLowerCase();
      const url = String(item.url || '').toLowerCase();
      return title.includes('litewater') || handle.includes('litewater') || url.includes('litewater');
    });
    const showLitewaterDisclaimer = remaining === 0 && hasLitewater;

    progress.hidden = false;
    progress.innerHTML = `
      <div class="cart-drawer__progress-copy">
        <strong>${remaining > 0
          ? `${formatMoney(remaining)} away from free shipping`
          : 'You unlocked free shipping'}
        </strong>
        <span>${percent}% there</span>
      </div>
      <div class="cart-drawer__progress-bar" aria-hidden="true">
        <div class="cart-drawer__progress-fill" style="width: ${percent}%;"></div>
      </div>
      ${showLitewaterDisclaimer ? `
        <p class="cart-drawer__progress-note">
          Free shipping excludes Litewater. Orders containing Litewater are not eligible for free shipping.
        </p>
      ` : ''}
    `;
  }

  function render(cart) {
    body.setAttribute('aria-busy', 'false');
    renderProgress(cart);

    if (!cart.items.length) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          Your cart is empty. Add a few favourites and they’ll show up here instantly.
        </div>
      `;
      if (progress) progress.hidden = true;
      footer.hidden = true;
      return;
    }

    const hasPreorder = cart.items.some(isPreorderItem);
    body.innerHTML = `
      ${hasPreorder ? `
        <div class="cart-drawer__preorder-note" role="note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v6"/><path d="M12 17h.01"/></svg>
          <p><strong>Your order contains a pre-order product.</strong> Your complete order will ship when the pre-order is ready.</p>
        </div>
      ` : ''}
      <div class="cart-drawer__items">
        ${cart.items.map(item => {
          const itemUrl = escapeHtml(safeStoreUrl(item.url));
          const imageUrl = escapeHtml(safeImageUrl(item.image));
          const title = escapeHtml(item.product_title);
          const itemKey = escapeHtml(item.key);
          return `
          <div class="cart-drawer__item" data-cart-key="${itemKey}">
            <a class="cart-drawer__item-image" href="${itemUrl}">
              ${imageUrl ? `<img src="${imageUrl}" alt="${title}" width="96" height="96">` : ''}
            </a>
            <div>
              <a class="cart-drawer__item-title" href="${itemUrl}">${title}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-drawer__item-variant">${escapeHtml(item.variant_title)}</div>` : ''}
              ${isPreorderItem(item) ? `<div class="cart-drawer__item-preorder">${escapeHtml(getPreorderLineMessage(item))}</div>` : ''}
              <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
              <div class="cart-drawer__qty">
                <button type="button" data-cart-qty-minus="${itemKey}" aria-label="Decrease quantity">-</button>
                <input type="number" min="0" value="${Number(item.quantity) || 0}" data-cart-qty-input="${itemKey}" aria-label="Quantity">
                <button type="button" data-cart-qty-plus="${itemKey}" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="cart-drawer__remove" data-cart-remove="${itemKey}">Remove</button>
            </div>
          </div>
        `; }).join('')}
      </div>
    `;
    subtotal.textContent = formatMoney(cart.total_price);
    footer.hidden = false;
  }

  async function refresh(openAfter = false) {
    if (openAfter) open();
    body.setAttribute('aria-busy', 'true');
    if (cachedCart) render(cachedCart);
    else body.innerHTML = `<div class="cart-drawer__loading">Loading your cart...</div>`;

    try {
      const cart = await syncPreorderOrderNote(await fetchCart());
      cachedCart = cart;
      render(cart);
      renderCartCount(cart);
    } catch {
      if (!cachedCart) body.innerHTML = `<div class="cart-drawer__empty">Your cart could not be loaded. Please try again.</div>`;
      body.setAttribute('aria-busy', 'false');
    }
  }

  async function updateItem(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();
      cachedCart = cart;
      render(cart);
      renderCartCount(cart);
    } catch {
      refresh();
    }
  }

  toggles.forEach(btn => btn.addEventListener('click', () => refresh(true)));
  closes.forEach(btn => btn.addEventListener('click', close));

  drawer.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-cart-qty-minus]');
    const plus = e.target.closest('[data-cart-qty-plus]');
    const remove = e.target.closest('[data-cart-remove]');

    if (minus) {
      const key = minus.dataset.cartQtyMinus;
      const input = drawer.querySelector(`[data-cart-qty-input="${CSS.escape(key)}"]`);
      const next = Math.max(0, (parseInt(input.value, 10) || 0) - 1);
      updateItem(key, next);
    }

    if (plus) {
      const key = plus.dataset.cartQtyPlus;
      const input = drawer.querySelector(`[data-cart-qty-input="${CSS.escape(key)}"]`);
      const next = (parseInt(input.value, 10) || 0) + 1;
      updateItem(key, next);
    }

    if (remove) {
      updateItem(remove.dataset.cartRemove, 0);
    }
  });

  drawer.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    updateItem(input.dataset.cartQtyInput, Math.max(0, parseInt(input.value, 10) || 0));
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  document.addEventListener('cart:updated', () => refresh(true));
  document.addEventListener('cart:pending', () => {
    open();
    if (cachedCart) render(cachedCart);
    else body.innerHTML = `<div class="cart-drawer__loading">Adding to your cart...</div>`;
  });
}

function renderStoredProductCard(product) {
  const productUrl = escapeHtml(safeStoreUrl(product.url));
  const imageUrl = escapeHtml(safeImageUrl(product.image));
  const isPreorder = product.preorder === true || product.preorder === 'true' || String(product.badge || '').toLowerCase() === 'pre-order';
  const preorderDate = product.preorderDate || '';
  return `
    <article class="product-card" aria-label="${escapeHtml(product.title)}">
      <div class="product-card__media">
        ${product.badge ? `<div class="product-badges product-badges--card" aria-label="Product badges"><span class="product-badge product-badge--${escapeHtml(product.badge.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))}">${escapeHtml(product.badge)}</span></div>` : ''}
        <a href="${productUrl}" class="product-card__image-wrap" aria-label="${escapeHtml(product.title)}">
          ${imageUrl ? `<img class="product-card__image" src="${imageUrl}" alt="${escapeHtml(product.title)}" width="600" height="600" loading="lazy">` : ''}
        </a>
        <button
          type="button"
          class="product-card__quick-view"
          data-quick-view
          data-product-url="${productUrl}"
          data-preorder="${isPreorder ? 'true' : 'false'}"
          data-preorder-date="${escapeHtml(preorderDate)}"
          aria-label="Quick view ${escapeHtml(product.title)}"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </button>
        <button
          class="product-card__atc btn--add"
          data-add-to-cart
          data-variant-id="${product.variantId}"
          data-preorder="${isPreorder ? 'true' : 'false'}"
          data-preorder-date="${escapeHtml(preorderDate)}"
          ${product.available
            ? `aria-label="Add ${escapeHtml(product.title)} to cart"`
            : `aria-label="${escapeHtml(product.title)} is out of stock" aria-disabled="true" data-sold-out="true" title="Out of stock"`}
        >
          ${product.available
            ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>'
            : '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'}
        </button>
      </div>
      <div class="product-card__body">
        ${product.vendor ? `<p class="product-card__vendor">${escapeHtml(product.vendor)}</p>` : ''}
        <h3 class="product-card__title"><a href="${productUrl}">${escapeHtml(product.title)}</a></h3>
        <div class="product-card__footer">
          <div class="product-card__price-wrap">
            <span class="product-card__price">${product.priceVaries ? 'From ' : ''}${formatMoney(product.price)}</span>
          </div>
        </div>
      </div>
    </article>
  `;
}

// ── QUICK VIEW ────────────────────────────────────────────────
function initQuickView() {
  const modal = $('.quick-view-modal');
  const body = $('[data-quick-view-body]', modal);
  const closes = $$('[data-quick-view-close]', modal);
  if (!modal || !body) return;

  let activeProduct = null;
  let activePreorder = null;

  function close() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    setBodyLock(false);
    deactivateFocusTrap(true, $('.quick-view-modal__panel', modal));
  }

  function open() {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    setBodyLock(true);
    activateFocusTrap($('.quick-view-modal__panel', modal), $('.quick-view-modal__close', modal));
  }

  function findSelectedVariant(product, variantId) {
    return product.variants.find(variant => String(variant.id) === String(variantId)) || product.variants.find(variant => variant.available) || product.variants[0];
  }

  function syncQuickViewNav() {
    const gallery = $('[data-quick-view-gallery]', body);
    const prev = $('[data-quick-view-prev]', body);
    const next = $('[data-quick-view-next]', body);
    if (!gallery || !prev || !next) return;

    const maxScroll = Math.max(0, gallery.scrollWidth - gallery.clientWidth);
    const current = Math.round(gallery.scrollLeft);

    prev.hidden = current <= 2;
    next.hidden = current >= maxScroll - 2;
  }

  function render(product, selectedVariantId) {
    activeProduct = product;
    const variant = findSelectedVariant(product, selectedVariantId);
    const description = stripHtml(product.description).slice(0, 220);
    const images = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
    const galleryImages = images.length ? images : (product.featured_image ? [product.featured_image] : []);
    const productUrl = escapeHtml(safeStoreUrl(product.url));
    const variantOptions = product.variants.map(item => `
      <option value="${item.id}" ${String(item.id) === String(variant.id) ? 'selected' : ''} ${item.available ? '' : 'disabled'}>
        ${escapeHtml(item.title)}${item.available ? '' : ' — Sold out'}
      </option>
    `).join('');

    body.innerHTML = `
      <div class="quick-view" data-quick-view-product-id="${product.id}">
        <div class="quick-view__media">
          ${galleryImages.length ? `
            <div class="quick-view__gallery-wrap">
              ${galleryImages.length > 1 ? `
                <button type="button" class="quick-view__nav quick-view__nav--prev" data-quick-view-prev aria-label="Previous image" hidden>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                    <path d="M15 18 9 12l6-6"/>
                  </svg>
                </button>
              ` : ''}
              <div class="quick-view__gallery" data-quick-view-gallery>
                ${galleryImages.map((image, index) => `
                  <div class="quick-view__slide">
                    <img src="${escapeHtml(safeImageUrl(image))}" alt="${escapeHtml(product.title)} image ${index + 1}" width="900" height="900" loading="lazy">
                  </div>
                `).join('')}
              </div>
              ${galleryImages.length > 1 ? `
                <button type="button" class="quick-view__nav quick-view__nav--next" data-quick-view-next aria-label="Next image">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </button>
              ` : ''}
            </div>
          ` : ''}
        </div>
        <div class="quick-view__content">
          ${product.vendor ? `<p class="quick-view__vendor">${escapeHtml(product.vendor)}</p>` : ''}
          <h2 class="quick-view__title">${escapeHtml(product.title)}</h2>
          <div class="quick-view__price">
            ${variant.compare_at_price > variant.price ? `<span class="quick-view__price-compare">${formatMoney(variant.compare_at_price)}</span>` : ''}
            <span class="quick-view__price-current" data-quick-view-price>${formatMoney(variant.price)}</span>
          </div>
          ${description ? `<p class="quick-view__description">${escapeHtml(description)}${stripHtml(product.description).length > 220 ? '…' : ''}</p>` : ''}
          ${product.variants.length > 1 ? `
            <label class="quick-view__option">
              <span class="quick-view__option-label">Variant</span>
              <select class="quick-view__select" data-quick-view-variant>
                ${variantOptions}
              </select>
            </label>
          ` : `<input type="hidden" data-quick-view-variant value="${variant.id}">`}
          <div class="quick-view__controls">
            <label>
              <span class="quick-view__qty-label">Qty</span>
              <input class="quick-view__qty-input" type="number" min="1" value="1" data-quick-view-qty>
            </label>
            <button type="button" class="btn btn--primary quick-view__submit" data-quick-view-submit ${variant.available ? '' : 'disabled'}>
              ${variant.available ? 'Add to Cart' : 'Sold Out'}
            </button>
          </div>
          <a class="quick-view__full-link" href="${productUrl}">
            View full product
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>
      </div>
    `;

    const gallery = $('[data-quick-view-gallery]', body);
    if (gallery) {
      gallery.addEventListener('scroll', syncQuickViewNav, { passive: true });
      requestAnimationFrame(syncQuickViewNav);
    }
  }

  async function openFromUrl(url, preorderDate = '') {
    activePreorder = preorderDate === null ? null : preorderDate;
    body.innerHTML = `<div class="quick-view-modal__loading">Loading product...</div>`;
    open();

    try {
      const res = await fetch(`${url}.js`);
      if (!res.ok) throw new Error('Unable to load product');
      const product = await res.json();
      render(product);
    } catch {
      body.innerHTML = `<div class="quick-view-modal__loading">Quick view is unavailable right now.</div>`;
    }
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      openFromUrl(trigger.dataset.productUrl, trigger.dataset.preorder === 'true' ? (trigger.dataset.preorderDate || '') : null);
      return;
    }

    const prev = e.target.closest('[data-quick-view-prev]');
    const next = e.target.closest('[data-quick-view-next]');
    if (prev || next) {
      e.preventDefault();
      const gallery = $('[data-quick-view-gallery]', body);
      if (gallery) {
        const distance = gallery.clientWidth;
        gallery.scrollBy({
          left: prev ? -distance : distance,
          behavior: 'smooth'
        });
      }
      return;
    }

    const submit = e.target.closest('[data-quick-view-submit]');
    if (!submit || !activeProduct) return;

    const variantInput = $('[data-quick-view-variant]', body);
    const qtyInput = $('[data-quick-view-qty]', body);
    const variantId = variantInput?.value;
    const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10));
    if (!variantId) return;

    const original = submit.textContent;
    submit.disabled = true;
    submit.textContent = 'Adding...';
    document.dispatchEvent(new CustomEvent('cart:pending'));

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity,
        properties: activePreorder !== null ? preorderLineProperties(activePreorder) : {}
      })
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Add failed');
        await res.json().catch(() => null);
        await syncPreorderOrderNote();
        submit.textContent = 'Added';
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(close, 300);
      })
      .catch(() => {
        submit.disabled = false;
        submit.textContent = original;
      });
  });

  body.addEventListener('change', (e) => {
    const select = e.target.closest('[data-quick-view-variant]');
    if (!select || !activeProduct) return;
    render(activeProduct, select.value);
  });

  closes.forEach((button) => button.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
  });
}

// ── STICKY MOBILE ATC ─────────────────────────────────────────
function initStickyMobileAtc() {
  const sticky = $('[data-sticky-atc]');
  if (!sticky) return;
  const trigger = $('[data-sticky-atc-trigger]', sticky);
  const form = $('.product-main .product-atc-form');
  const mainButton = $('.product-main .product-atc');
  const price = $('.product-price');
  const stickyPrice = $('.product-sticky-atc__price', sticky);
  if (!sticky || !form || !mainButton || !trigger) return;

  const syncPrice = () => {
    if (stickyPrice && price) stickyPrice.textContent = price.textContent;
  };

  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  const observer = new IntersectionObserver(([entry]) => {
    const shouldShow = isMobile() && !entry.isIntersecting;
    sticky.hidden = !shouldShow;
    sticky.classList.toggle('is-visible', shouldShow);
  }, { threshold: 0.35 });

  observer.observe(form);
  syncPrice();
  window.addEventListener('resize', syncPrice, { passive: true });
  new MutationObserver(syncPrice).observe(price, { childList: true, characterData: true, subtree: true });

  trigger.addEventListener('click', () => {
    syncPrice();
    form.requestSubmit();
  });
}

// ── RECENTLY VIEWED ───────────────────────────────────────────
function initRecentlyViewed() {
  const productRoot = $('.product-main[data-product-handle]');
  const section = $('[data-recently-viewed]');
  const track = $('[data-recently-viewed-track]');
  if (!productRoot || !section || !track) return;

  const storageKey = 'lwco-recently-viewed';
  const current = {
    handle: productRoot.dataset.productHandle,
    url: productRoot.dataset.productUrl,
    title: productRoot.dataset.productTitle,
    vendor: productRoot.dataset.productVendor,
    price: Number(productRoot.dataset.productPrice || 0),
    priceVaries: productRoot.dataset.productPriceVaries === 'true',
    image: productRoot.dataset.productImage,
    variantId: productRoot.dataset.productVariantId,
    available: productRoot.dataset.productAvailable === 'true',
    badge: productRoot.dataset.productBadge || '',
    preorder: productRoot.dataset.productPreorder === 'true',
    preorderDate: productRoot.dataset.productPreorderDate || ''
  };

  let items = [];
  try {
    items = JSON.parse(localStorage.getItem(storageKey) || '[]');
  } catch {
    items = [];
  }

  items = [current, ...items.filter(item => item.handle !== current.handle)].slice(0, 8);
  localStorage.setItem(storageKey, JSON.stringify(items));

  const related = items.filter(item => item.handle !== current.handle).slice(0, 6);
  if (!related.length) return;

  track.innerHTML = related.map(renderStoredProductCard).join('');
  section.hidden = false;
}

// ── FEATURED COLLECTIONS TABS ───────────────────────────────────
function initFeaturedTabs() {
  $$('.featured-tabs').forEach(section => {
    const tabs   = $$('.featured-tabs__tab', section);
    const panels = $$('.featured-tabs__panel', section);
    if (!tabs.length) return;

    // Inactive tabs ship empty (see featured-collections-tabs.liquid) and fetch
    // their cards once — on first view, or in the background once the page is idle.
    function loadPanel(panel) {
      const track = $('[data-tab-src]', panel);
      if (!track || track._tabLoad) return track?._tabLoad;
      track._tabLoad = fetch(track.dataset.tabSrc)
        .then(res => (res.ok ? res.text() : Promise.reject()))
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const limit = parseInt(track.dataset.tabLimit, 10) || 8;
          const cards = $$('[data-tab-products] .product-card', doc).slice(0, limit);
          const viewAll = $('.featured-tabs__view-all-card', track);
          cards.forEach(card => track.insertBefore(document.importNode(card, true), viewAll));
          track.removeAttribute('data-tab-src');
          window.LWWishlistAPI?.repaint?.();
        })
        .catch(() => { track._tabLoad = null; });
      return track._tabLoad;
    }

    function activate(idx) {
      tabs.forEach((t, i) => {
        const active = i === idx;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', String(active));
        t.toggleAttribute('tabindex', !active);
        if (!active) t.setAttribute('tabindex', '-1');
      });
      panels.forEach((p, i) => {
        const active = i === idx;
        p.classList.toggle('is-active', active);
        p.hidden = !active;
        if (active) {
          loadPanel(p);
          const viewport = $('.featured-tabs__viewport', p);
          if (viewport) viewport.scrollLeft = 0;
          p.style.animation = 'fadeIn 0.3s ease';
          setTimeout(() => { p.style.animation = ''; }, 300);
        }
      });
    }

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(i));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const nextIndex = event.key === 'ArrowRight' ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
        activate(nextIndex);
        tabs[nextIndex].focus();
      });
    });

    activate(0);

    const preload = () => panels.forEach(loadPanel);
    if (document.readyState === 'complete') setTimeout(preload, 1500);
    else window.addEventListener('load', () => setTimeout(preload, 1500), { once: true });
  });
}

// ── GOALS MARQUEE MANUAL SCROLL ──────────────────────────────
function initGoalsMarqueeScroll() {
  $$('.goals-marquee__viewport').forEach((viewport) => {
    const track = $('.goals-marquee__track', viewport);
    if (!track) return;

    const originals = [...track.children];
    if (originals.length < 2) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    originals.forEach((card) => {
      const leadingClone = card.cloneNode(true);
      const trailingClone = card.cloneNode(true);
      leadingClone.setAttribute('aria-hidden', 'true');
      trailingClone.setAttribute('aria-hidden', 'true');
      leadingClone.setAttribute('tabindex', '-1');
      trailingClone.setAttribute('tabindex', '-1');
      before.appendChild(leadingClone);
      after.appendChild(trailingClone);
    });
    track.prepend(before);
    track.append(after);

    let loopStart = 0;
    let loopWidth = 0;
    let isPointerDown = false;
    let didDrag = false;
    let suppressClick = false;
    let startX = 0;
    let startScrollLeft = 0;

    function measureLoop(resetPosition = false) {
      const previousStart = loopStart;
      const previousWidth = loopWidth;
      const previousOffset = previousWidth
        ? ((viewport.scrollLeft - previousStart) % previousWidth + previousWidth) % previousWidth
        : 0;

      loopStart = originals[0].offsetLeft;
      loopWidth = track.children[originals.length * 2].offsetLeft - loopStart;
      if (resetPosition || !previousWidth) {
        viewport.scrollLeft = loopStart;
      } else {
        viewport.scrollLeft = loopStart + previousOffset * (loopWidth / previousWidth);
      }
    }

    function keepInLoop() {
      if (!loopWidth) return;
      if (viewport.scrollLeft >= loopStart + loopWidth) viewport.scrollLeft -= loopWidth;
      if (viewport.scrollLeft < loopStart) viewport.scrollLeft += loopWidth;
    }

    requestAnimationFrame(() => {
      measureLoop(true);
    });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => measureLoop(false), 160);
    });
    viewport.addEventListener('scroll', keepInLoop, { passive: true });

    viewport.addEventListener('pointerdown', (event) => {
      isPointerDown = true;
      didDrag = false;
      startX = event.clientX;
      startScrollLeft = viewport.scrollLeft;
      viewport.classList.add('is-interacting');
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!isPointerDown) return;
      const delta = event.clientX - startX;
      if (!didDrag && Math.abs(delta) < 6) return;
      if (!didDrag) {
        didDrag = true;
        viewport.classList.add('is-dragging');
      }
      viewport.scrollLeft = startScrollLeft - delta;
    });

    function endDrag(event) {
      if (!isPointerDown) return;
      isPointerDown = false;
      viewport.classList.remove('is-dragging');
      clearTimeout(viewport._goalsInteractionTimer);
      viewport._goalsInteractionTimer = setTimeout(() => {
        viewport.classList.remove('is-interacting');
      }, 120);
      if (didDrag) {
        suppressClick = true;
        setTimeout(() => { suppressClick = false; }, 0);
      }
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', endDrag);
    viewport.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);
  });
}

// ── SCROLL ANIMATIONS ───────────────────────────────────────────
function initScrollAnimations() {
  const els = $$('.animate-on-scroll');
  if (!els.length) return;

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  els.forEach(el => io.observe(el));
}

// ── ADD TO CART ─────────────────────────────────────────────────
function initAddToCart() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-to-cart]');
    if (!btn) return;

    if (btn.dataset.soldOut === 'true') {
      e.preventDefault();
      showSoldOut(btn);
      return;
    }

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const original = btn.innerHTML;
    btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>`;
    document.dispatchEvent(new CustomEvent('cart:pending'));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: btn.dataset.preorder === 'true' ? preorderLineProperties(btn.dataset.preorderDate) : {}
        }),
      });

      if (res.ok) {
        await res.json().catch(() => null);
        await syncPreorderOrderNote();
        btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>`;
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => {
          btn.innerHTML  = original;
          btn.disabled   = false;
        }, 1800);
      } else {
        throw new Error('Add to cart failed');
      }
    } catch {
      btn.innerHTML = original;
      btn.disabled  = false;
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  });
}

// Sold-out quick-add buttons expand into an "Out of stock" pill when clicked.
function showSoldOut(btn) {
  if (!btn._soldOutIcon) btn._soldOutIcon = btn.innerHTML;
  clearTimeout(btn._soldOutTimer);
  btn.classList.add('is-sold-out-shown');
  btn.innerHTML = '<span class="btn--add__sold-out">Out of stock</span>';

  let live = document.getElementById('sold-out-live');
  if (!live) {
    live = document.createElement('div');
    live.id = 'sold-out-live';
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');
    document.body.appendChild(live);
  }
  live.textContent = '';
  requestAnimationFrame(() => { live.textContent = btn.getAttribute('aria-label') || 'Out of stock'; });

  btn._soldOutTimer = setTimeout(() => {
    btn.classList.remove('is-sold-out-shown');
    btn.innerHTML = btn._soldOutIcon;
  }, 2200);
}

// ── CART COUNT ──────────────────────────────────────────────────
async function updateCartCount() {
  try {
    const res  = await fetch('/cart.js');
    const cart = await res.json();
    renderCartCount(cart);
  } catch { /* silent */ }
}

// ── NEWSLETTER FORM ──────────────────────────────────────────────
function initNewsletterForm() {
  $$('.newsletter__form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('.newsletter__input');
      const btn   = form.querySelector('[type="submit"]');
      if (!input?.value) return;

      btn.textContent = 'Subscribing…';
      btn.disabled    = true;

      // Shopify newsletter form submission
      try {
        const fd = new FormData();
        fd.append('contact[email]', input.value);
        fd.append('form_type', 'customer');

        await fetch('/', { method: 'POST', body: fd });

        btn.textContent = 'You\'re in!';
        input.value     = '';

        setTimeout(() => {
          btn.textContent = 'Subscribe';
          btn.disabled    = false;
        }, 3000);
      } catch {
        btn.textContent = 'Subscribe';
        btn.disabled    = false;
      }
    });
  });
}

// ── SMOOTH ANCHOR SCROLL ────────────────────────────────────────
function initSmoothScroll() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

// ── TICKER DUPLICATE ────────────────────────────────────────────
function initTicker() {
  // Liquid template already duplicates items for seamless CSS loop — nothing to do here
}

// ── HERO SLIDESHOW ─────────────────────────────────────────────
function initHeroSlideshow() {
  $$('[data-hero-slideshow]').forEach((hero) => {
    const slides = $$('[data-hero-slide]', hero);
    const dots = $$('[data-hero-dot]', hero);
    const previous = $('[data-hero-previous]', hero);
    const next = $('[data-hero-next]', hero);
    const pause = $('[data-hero-pause]', hero);
    if (slides.length < 2) return;

    let activeIndex = 0;
    let autoplayTimer = null;
    const autoplay = hero.dataset.autoplay === 'true';
    const delay = Number(hero.dataset.autoplayDelay) || 7000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let manuallyPaused = reducedMotion;
    hero.style.setProperty('--hero-autoplay-delay', `${delay}ms`);

    function showSlide(index) {
      activeIndex = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', !isActive);
      });

      dots.forEach((dot) => dot.classList.remove('is-active'));
      void hero.offsetWidth;

      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeIndex;
        if (isActive) dot.classList.add('is-active');
        dot.setAttribute('aria-current', String(isActive));
      });
    }

    function stopAutoplay() {
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }

    function startAutoplay() {
      stopAutoplay();
      if (!autoplay || reducedMotion || manuallyPaused || document.hidden) return;
      autoplayTimer = window.setInterval(() => showSlide(activeIndex + 1), delay);
    }

    function setPaused(paused) {
      manuallyPaused = paused;
      hero.classList.toggle('is-paused', paused);
      if (pause) {
        pause.classList.toggle('is-paused', paused);
        pause.setAttribute('aria-pressed', String(paused));
        pause.setAttribute('aria-label', paused ? 'Play hero slides' : 'Pause hero slides');
      }
      if (paused) stopAutoplay();
      else startAutoplay();
    }

    previous?.addEventListener('click', () => {
      showSlide(activeIndex - 1);
      startAutoplay();
    });

    next?.addEventListener('click', () => {
      showSlide(activeIndex + 1);
      startAutoplay();
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        showSlide(Number(dot.dataset.heroDot));
        startAutoplay();
      });
    });

    pause?.addEventListener('click', () => setPaused(!manuallyPaused));

    hero.addEventListener('mouseenter', stopAutoplay);
    hero.addEventListener('mouseleave', startAutoplay);

    hero.addEventListener('focusin', stopAutoplay);
    hero.addEventListener('focusout', startAutoplay);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    showSlide(0);
    if (reducedMotion) setPaused(true);
    startAutoplay();
  });
}

// The media plane is oversized in CSS so this bounded movement cannot expose its edges.
function initHeroParallax() {
  const hero = $('.hero');
  if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let ticking = false;

  function update() {
    const heroHeight = hero.offsetHeight;
    const progress = Math.min(Math.max(window.scrollY, 0), heroHeight) / heroHeight;
    hero.style.setProperty('--hero-parallax-y', `${progress * heroHeight * 0.06}px`);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });

  window.addEventListener('resize', update);
  update();
}

function initCollectionFilters() {
  const filters = $('[data-collection-filters]');
  const overlay = $('.collection-filters__overlay');
  const openButton = $('[data-collection-filters-open]');
  const closeButtons = $$('[data-collection-filters-close]');
  const form = $('[data-collection-filter-form]');
  const sort = $('[data-collection-sort]');
  const isMobile = () => window.matchMedia('(max-width: 900px)').matches;

  function syncFilterAccessibility() {
    if (!filters) return;
    if (isMobile() && !filters.classList.contains('is-open')) filters.setAttribute('inert', '');
    else filters.removeAttribute('inert');
  }

  function close() {
    filters?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    openButton?.setAttribute('aria-expanded', 'false');
    if (isMobile()) filters?.setAttribute('inert', '');
    setBodyLock(false);
    deactivateFocusTrap(true, filters);
  }

  openButton?.addEventListener('click', () => {
    filters?.classList.add('is-open');
    filters?.removeAttribute('inert');
    overlay?.classList.add('is-open');
    openButton.setAttribute('aria-expanded', 'true');
    setBodyLock(true);
    const closeButton = $('[data-collection-filters-close]', filters);
    activateFocusTrap(filters, closeButton);
    window.setTimeout(() => closeButton?.focus(), 0);
  });

  closeButtons.forEach((button) => button.addEventListener('click', close));
  form?.addEventListener('change', (event) => {
    if (event.target.matches('[data-filter-auto-submit]') && window.innerWidth > 900) form.submit();
  });
  sort?.addEventListener('change', () => sort.form.submit());

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && filters?.classList.contains('is-open')) close();
  });

  window.addEventListener('resize', syncFilterAccessibility, { passive: true });
  syncFilterAccessibility();
}

// ── PRODUCT CARD QUICK ADD FEEDBACK ─────────────────────────────
function initProductCardFeedback() {
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.product-card');
    if (!card || e.target.closest('[data-add-to-cart]') || e.target.closest('[data-quick-view]')) return;
    const link = card.querySelector('a[href]');
    if (link) window.location.href = link.href;
  });
}

// ── PRODUCT PAGE FORM ────────────────────────────────────────────
function initProductForm() {
  const form = document.querySelector('.product-atc-form');
  if (!form) return;
  if (form.closest('.product-main')) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>`;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: form.querySelector('[name="id"]').value,
          quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1'),
        }),
      });

      if (res.ok) {
        btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>&nbsp; Added!`;
        updateCartCount();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        setTimeout(() => { btn.innerHTML = original; btn.disabled = false; }, 2000);
      } else {
        throw new Error();
      }
    } catch {
      btn.innerHTML = original;
      btn.disabled = false;
    }
  });
}

function initPreorderCheckoutNoteSync() {
  document.addEventListener('click', async (event) => {
    const checkoutLink = event.target.closest('.cart-drawer__checkout, .cart-checkout');
    if (!checkoutLink || checkoutLink.dataset.preorderNoteSynced === 'true') return;

    event.preventDefault();
    checkoutLink.dataset.preorderNoteSynced = 'true';
    await syncPreorderOrderNote();
    window.location.href = checkoutLink.href;
  });
}

// ── PRODUCT VARIANT AVAILABILITY GUARD ─────────────────────────
function initProductVariantAvailabilityGuard() {
  const form = document.querySelector('.product-main .product-atc-form');
  const variantsJson = document.getElementById('product-variants-json');
  if (!form || !variantsJson) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsJson.textContent || '[]');
  } catch {
    return;
  }

  const selectedVariant = () => {
    const variantId = form.querySelector('[name="id"]')?.value;
    return variants.find(variant => String(variant.id) === String(variantId));
  };

  const sync = () => {
    const variant = selectedVariant();
    const isAvailable = Boolean(variant?.available);
    const atcButton = form.querySelector('.product-atc');
    const dynamicCheckout = form.querySelector('.product-dynamic-checkout');
    const stickyButton = document.querySelector('[data-sticky-atc-trigger]');

    if (atcButton) {
      atcButton.disabled = !isAvailable;
      atcButton.setAttribute('aria-disabled', String(!isAvailable));
      if (!isAvailable) atcButton.textContent = 'Sold Out';
    }

    if (dynamicCheckout) dynamicCheckout.hidden = !isAvailable;

    if (stickyButton) {
      stickyButton.disabled = !isAvailable;
      stickyButton.setAttribute('aria-disabled', String(!isAvailable));
      if (!isAvailable) stickyButton.textContent = 'Sold Out';
    }

    return isAvailable;
  };

  form.addEventListener('submit', (event) => {
    if (sync()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.product-option__value')) return;
    requestAnimationFrame(sync);
  });

  sync();
}

// ── POLICY TOC ───────────────────────────────────────────────────
function initPolicyHeader() {
  const titleEl = document.querySelector('.shopify-policy__title');
  if (!titleEl) return;
  const date = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date('2026-05-19'));
  const p = document.createElement('p');
  p.className = 'policy-updated';
  p.textContent = 'Last updated ' + date;
  titleEl.appendChild(p);
}

function initPolicyToc() {
  const policyBody = document.querySelector('.shopify-policy__body');
  if (!policyBody) return;
  const rte = policyBody.querySelector('.rte');
  if (!rte) return;

  const headings = [...rte.querySelectorAll('h2, h3')].filter(h => h.textContent.trim());
  headings.forEach((h, i) => {
    h.id = 'section-' + i + '-' + h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  });

  const content = document.createElement('div');
  content.className = 'policy-content';

  // Short policy — no sidebar, just centered reading column
  if (headings.length < 4) {
    const wrap = document.createElement('div');
    wrap.className = 'policy-body-simple';
    wrap.appendChild(content);
    policyBody.innerHTML = '';
    content.appendChild(rte);
    policyBody.appendChild(wrap);
    return;
  }

  // Full policy — sidebar + content
  const sidebar = document.createElement('aside');
  sidebar.className = 'policy-sidebar';
  sidebar.innerHTML = '<p class="policy-sidebar__label">Contents</p>'
    + '<ul class="policy-sidebar__list">'
    + headings.map(h => `<li><a class="policy-sidebar__link" href="#${h.id}">${h.textContent.trim()}</a></li>`).join('')
    + '</ul>';

  const layout = document.createElement('div');
  layout.className = 'policy-layout';
  layout.appendChild(sidebar);
  layout.appendChild(content);

  policyBody.innerHTML = '';
  content.appendChild(rte);
  policyBody.appendChild(layout);

  const links = [...sidebar.querySelectorAll('.policy-sidebar__link')];
  const io = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      links.forEach(l => l.classList.remove('is-active'));
      const match = links.find(l => l.getAttribute('href') === '#' + entry.target.id);
      if (match) match.classList.add('is-active');
    });
  }, { rootMargin: '-10% 0px -80% 0px' });

  headings.forEach(h => io.observe(h));
}

// ── PRODUCT SUGGESTION FORM ─────────────────────────────────────
function initProductSuggestionForm() {
  const dialog = document.querySelector('[data-product-suggestion-dialog]');
  if (!dialog) return;

  const openButtons = document.querySelectorAll('[data-product-suggestion-open]');
  const closeButtons = dialog.querySelectorAll('[data-product-suggestion-close]');

  const openDialog = () => {
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.body.classList.add('product-suggestion-is-open');
  };

  const closeDialog = () => {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('product-suggestion-is-open');
  };

  openButtons.forEach(button => button.addEventListener('click', openDialog));
  closeButtons.forEach(button => button.addEventListener('click', closeDialog));

  dialog.addEventListener('click', event => {
    const bounds = dialog.getBoundingClientRect();
    const outside = event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom;
    if (outside) closeDialog();
  });

  dialog.addEventListener('close', () => {
    document.body.classList.remove('product-suggestion-is-open');
  });

  if (dialog.querySelector('[data-product-suggestion-auto-open]')) openDialog();
}

// ── SHOP MENU COLLECTION PREVIEWS ──────────────────────────────
function initShopMenuPreviews() {
  document.querySelectorAll('.nav-dropdown--wide').forEach(menu => {
    const triggers = [...menu.querySelectorAll('[data-menu-preview]')];
    const views = [...menu.querySelectorAll('[data-menu-preview-view]')];
    if (!triggers.length || !views.length) return;

    const activate = name => {
      triggers.forEach(trigger => trigger.classList.toggle('is-previewed', trigger.dataset.menuPreview === name));
      views.forEach(view => {
        const active = view.dataset.menuPreviewView === name;
        view.classList.toggle('is-active', active);
        view.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    };

    triggers.forEach(trigger => {
      trigger.addEventListener('mouseenter', () => activate(trigger.dataset.menuPreview));
      trigger.addEventListener('focus', () => activate(trigger.dataset.menuPreview));
    });
    menu.querySelectorAll('.nav-dropdown__col .nav-dropdown__link:not([data-menu-preview])').forEach(link => {
      link.addEventListener('mouseenter', () => activate('goals'));
      link.addEventListener('focus', () => activate('goals'));
    });
    menu.addEventListener('mouseleave', () => activate('goals'));
  });
}

// ── CUSTOMER PRIVACY CONTROLS ─────────────────────────────────
function initCustomerPrivacyControls() {
  const button = $('[data-cookie-preferences]');
  if (!button || !window.Shopify?.loadFeatures) return;

  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
    if (error || !window.Shopify.customerPrivacy) return;
    const privacy = window.Shopify.customerPrivacy;
    const preferencesAvailable = privacy.shouldShowBanner?.() || privacy.saleOfDataRegion?.();
    button.hidden = !preferencesAvailable;
  });

  button.addEventListener('click', () => {
    if (window.privacyBanner?.showPreferences) window.privacyBanner.showPreferences();
  });
}

function initShopifyChatAccessibility() {
  const labelChatButton = () => {
    const host = document.querySelector('#ShopifyChat');
    const button = host?.shadowRoot?.querySelector('.chat-toggle') || document.querySelector('#ShopifyChat .chat-toggle');
    if (!button || button.getAttribute('aria-label')) return Boolean(button);
    button.setAttribute('aria-label', 'Chat with customer support');
    button.setAttribute('title', 'Chat with customer support');
    return true;
  };

  labelChatButton();
  const observer = new MutationObserver(() => {
    labelChatButton();
  });
  observer.observe(document.body, { attributes: true, childList: true, subtree: true });
  const interval = window.setInterval(labelChatButton, 100);
  window.setTimeout(() => {
    observer.disconnect();
    window.clearInterval(interval);
  }, 60000);
}

// ── INIT ─────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initAnnouncementBar();
  initStickyHeader();
  initAccountMenu();
  initMobileNav();
  initSearchForms();
  initSearchDrawer();
  initSearchPageRewrite();
  initCartDrawer();
  initQuickView();
  initFeaturedTabs();
  initGoalsMarqueeScroll();
  initScrollAnimations();
  initAddToCart();
  initProductForm();
  initPreorderCheckoutNoteSync();
  initStickyMobileAtc();
  initRecentlyViewed();
  initNewsletterForm();
  initSmoothScroll();
  initTicker();
  initHeroSlideshow();
  initHeroParallax();
  initCollectionFilters();
  initProductCardFeedback();
  initProductVariantAvailabilityGuard();
  updateCartCount();
  initPolicyHeader();
  initPolicyToc();
  initProductSuggestionForm();
  initShopMenuPreviews();
  initCustomerPrivacyControls();
  initShopifyChatAccessibility();
});
